import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card.js";
import { Button } from "../components/ui/button.js";
import { Badge } from "../components/ui/badge.js";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "../components/ui/table.js";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog.js";
import { Eye, Trash2, Download, RefreshCw, AlertTriangle, ShieldAlert, CheckCircle2, Sparkles, AlertCircle, Info, Layers } from "lucide-react";
import { toast } from "sonner";

interface DocSimulado {
  folio: number;
  tipo: string;
  receptor: string;
  monto: number;
  estado: "EMITIDO" | "ANULADO_MANUAL" | "ANULADO_NC" | "ERROR" | "SIN_PDF";
}

const DOCS_DEMO: DocSimulado[] = [
  { folio: 36359, tipo: "BE Exenta (41)", receptor: "Consumidor Final", monto: 50000, estado: "EMITIDO" },
  { folio: 36360, tipo: "BE Afecta (39)", receptor: "Empresa de Servicios SpA", monto: 120000, estado: "EMITIDO" },
  { folio: 36341, tipo: "BE Afecta (39)", receptor: "Consumidor Final", monto: 35000, estado: "ANULADO_MANUAL" },
  { folio: 120, tipo: "Factura (33)", receptor: "Distribuidora Los Andes Ltda.", monto: 450000, estado: "ANULADO_NC" },
  { folio: 0, tipo: "BE Afecta (39)", receptor: "Consumidor Final", monto: 1, estado: "ERROR" },
  { folio: 36342, tipo: "BE Afecta (39)", receptor: "Consumidor Final", monto: 1, estado: "SIN_PDF" },
];

export default function MockupAccionesPage() {
  const [selectedVariant, setSelectedVariant] = useState<"A" | "B" | "C" | "D">("C");
  const [selectedPropuesta, setSelectedPropuesta] = useState<"1" | "2" | "3">("1");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState<Set<number>>(new Set([36359, 36360]));

  const toggleSelect = (folio: number) => {
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(folio)) next.delete(folio);
      else next.add(folio);
      return next;
    });
  };

  // ── Renderizado de Badges de Estado según la Propuesta elegida ──
  const renderBadge = (estado: DocSimulado["estado"]) => {
    // ── PROPUESTA 1: Jerarquía Semántica Calibrada (Recomendada) ──
    if (selectedPropuesta === "1") {
      switch (estado) {
        case "EMITIDO":
          return <Badge variant="success" className="bg-emerald-100/80 text-emerald-800 border border-emerald-200">Emitido</Badge>;
        case "ANULADO_MANUAL":
          return (
            <Badge variant="outline" className="border-amber-300 text-amber-800 bg-amber-50/80 font-medium">
              Anulada manual
            </Badge>
          );
        case "ANULADO_NC":
          // Nivel 1 (Piola): Hecho contable histórico pasado, NO rojo destructivo
          return (
            <Badge variant="outline" className="border-rose-200 text-rose-700 bg-rose-50/80 font-medium">
              Anulada (NC)
            </Badge>
          );
        case "ERROR":
          // Nivel 2 (Medio): Advertencia de atención sin ser bomba atómica
          return (
            <Badge variant="outline" className="border-orange-300 text-orange-800 bg-orange-100 font-semibold gap-1">
              <AlertCircle className="h-3 w-3 text-orange-600" />
              Error
            </Badge>
          );
        case "SIN_PDF":
          return (
            <Badge variant="outline" className="border-yellow-300 text-yellow-800 bg-yellow-50 font-medium gap-1">
              <Info className="h-3 w-3 text-yellow-600" />
              Sin PDF
            </Badge>
          );
      }
    }

    // ── PROPUESTA 2: Registro Histórico Neutro (Estilo Contable Pizarra) ──
    if (selectedPropuesta === "2") {
      switch (estado) {
        case "EMITIDO":
          return <Badge variant="success">Emitido</Badge>;
        case "ANULADO_MANUAL":
          return (
            <Badge variant="outline" className="border-slate-300 text-slate-700 bg-slate-100 font-medium">
              Anulada manual
            </Badge>
          );
        case "ANULADO_NC":
          // Nivel 1 (Piola): Archivada en gris neutro sin fatiga visual
          return (
            <Badge variant="outline" className="border-slate-300 text-slate-600 bg-slate-100 font-medium">
              Anulada (NC)
            </Badge>
          );
        case "ERROR":
          // Nivel 2 (Medio): Alerta suave en rose-100
          return (
            <Badge variant="outline" className="border-rose-300 text-rose-800 bg-rose-100 font-medium">
              Error
            </Badge>
          );
        case "SIN_PDF":
          return <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">Sin PDF</Badge>;
      }
    }

    // ── PROPUESTA 3: Alto Contraste con Indicadores ──
    switch (estado) {
      case "EMITIDO":
        return <Badge variant="success">Emitido</Badge>;
      case "ANULADO_MANUAL":
        return <Badge variant="outline" className="border-2 border-amber-400 text-amber-800 bg-amber-50">Anulada manual</Badge>;
      case "ANULADO_NC":
        return <Badge variant="outline" className="border-2 border-rose-400 text-rose-800 bg-rose-50">Anulada (NC)</Badge>;
      case "ERROR":
        return <Badge variant="destructive" className="bg-red-500 font-bold">Error</Badge>;
      case "SIN_PDF":
        return <Badge variant="warning">Sin PDF</Badge>;
    }
  };

  // ── Renderizado de Botones de Acción (con fondo real garantizado, nunca blancos) ──
  const renderActionButtons = (doc: DocSimulado) => {
    const isAnulable = doc.estado === "EMITIDO";

    // ── VARIANTE A: Pill Pastel con Contraste Marcado ──
    if (selectedVariant === "A") {
      return (
        <div className="flex gap-1.5 items-center">
          <button
            onClick={() => toast.info(`Abriendo visor para folio ${doc.folio}`)}
            title="Ver documento en pestaña nueva"
            className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-teal-100 border border-teal-300 text-teal-900 hover:bg-teal-200 transition-all shadow-xs cursor-pointer"
          >
            <Eye className="h-4 w-4" />
          </button>
          {isAnulable && (
            <button
              onClick={() => setModalOpen(true)}
              title="Anular con Nota de Crédito"
              className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-rose-100 border border-rose-300 text-rose-700 hover:bg-rose-200 hover:border-rose-400 transition-all shadow-xs cursor-pointer"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      );
    }

    // ── VARIANTE B: Outline de Alto Contraste (2px) ──
    if (selectedVariant === "B") {
      return (
        <div className="flex gap-1.5 items-center">
          <button
            onClick={() => toast.info(`Abriendo visor para folio ${doc.folio}`)}
            title="Ver documento en pestaña nueva"
            className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white dark:bg-card border-2 border-teal-600 text-teal-700 hover:bg-teal-600 hover:text-white transition-all shadow-xs cursor-pointer font-bold"
          >
            <Eye className="h-4 w-4" />
          </button>
          {isAnulable && (
            <button
              onClick={() => setModalOpen(true)}
              title="Anular con Nota de Crédito"
              className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-white dark:bg-card border-2 border-rose-500 text-rose-600 hover:bg-rose-600 hover:text-white transition-all shadow-xs cursor-pointer font-bold"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      );
    }

    // ── VARIANTE C: Botón con Icono + Texto Explícito (Garantizado con Fondo Real) ──
    if (selectedVariant === "C") {
      return (
        <div className="flex gap-2 items-center">
          <button
            onClick={() => toast.info(`Abriendo visor para folio ${doc.folio}`)}
            title="Ver documento en pestaña nueva"
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-teal-100/70 border border-teal-300 text-teal-900 hover:bg-teal-200 hover:border-teal-400 dark:bg-teal-950/60 dark:border-teal-700 dark:text-teal-200 text-xs font-semibold transition-all shadow-xs cursor-pointer"
          >
            <Eye className="h-3.5 w-3.5 text-teal-800 dark:text-teal-300" />
            <span>Ver</span>
          </button>
          {isAnulable && (
            <button
              onClick={() => setModalOpen(true)}
              title="Abrir flujo de anulación"
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-rose-100/70 border border-rose-300 text-rose-800 hover:bg-rose-200 hover:border-rose-400 dark:bg-rose-950/60 dark:border-rose-700 dark:text-rose-200 text-xs font-semibold transition-all shadow-xs cursor-pointer"
            >
              <Trash2 className="h-3.5 w-3.5 text-rose-700 dark:text-rose-300" />
              <span>Anular</span>
            </button>
          )}
        </div>
      );
    }

    // ── VARIANTE D: Botones Sólidos Matizados ──
    return (
      <div className="flex gap-1.5 items-center">
        <button
          onClick={() => toast.info(`Abriendo visor para folio ${doc.folio}`)}
          className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-slate-700 text-white hover:bg-slate-900 transition-all shadow-xs cursor-pointer"
        >
          <Eye className="h-4 w-4" />
        </button>
        {isAnulable && (
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-rose-600 text-white hover:bg-rose-700 transition-all shadow-xs cursor-pointer"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto pb-16">
      {/* Encabezado */}
      <div className="border-b pb-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="p-1.5 rounded-lg bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-400">
            <Sparkles className="h-5 w-5" />
          </span>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            Laboratorio de Calibración: Los 3 Niveles Reales de Destrucción
          </h1>
        </div>
        <p className="text-muted-foreground text-sm">
          Evaluación y propuestas interactivas para eliminar los botones blancos y calibrar los 3 niveles de alerta (Piola, Medio y Destructivo Destructivo) en Estados y Acciones.
        </p>
      </div>

      {/* Diagnóstico Crítico: ¿Por qué quedaron blancos los botones oficiales? */}
      <Card className="border-rose-200 bg-rose-50/20 dark:bg-rose-950/10 dark:border-rose-900/40">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2 text-rose-700 dark:text-rose-400">
            <AlertTriangle className="h-5 w-5" />
            <CardTitle className="text-base font-bold">Diagnóstico Técnico: ¿Por qué los botones se veían blancos en la foto oficial?</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground space-y-2 leading-relaxed">
          <p>
            • En el Mockup (Foto 1), los botones usaban la etiqueta nativa <code>&lt;button&gt;</code> con fondos directos <code>bg-teal-50</code> y <code>bg-rose-50</code>, por eso <strong>sí tenían color pastel visible</strong>.
          </p>
          <p>
            • En la página oficial <code>/emitidos</code> (Foto 2), se usó el componente <code>&lt;Button variant="outline"&gt;</code>. La clase <code>.fl-btn-outline</code> en <code>index.css</code> tiene definida la regla <code>background: transparent; border: 1px solid #e2e8f0;</code>, la cual <strong>aplastó y borró el color de fondo pastel</strong>, dejando el botón completamente blanco y deslavado sobre la fila.
          </p>
          <p className="text-foreground font-semibold">
            ✅ Solución: Aplicar el fondo pastel con <code>bg-teal-100/70</code> y <code>bg-rose-100/70</code> sin la regla <code>variant="outline"</code> que lo anula.
          </p>
        </CardContent>
      </Card>

      {/* Explicación de los 3 Niveles Reales de Destrucción */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Nivel 1 */}
        <Card className="border-border/60">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Nivel 1</span>
              <Badge variant="outline" className="text-teal-800 border-teal-300 bg-teal-50">Piola / Informativo</Badge>
            </div>
            <CardTitle className="text-base">Destructivo Piola (Reposo)</CardTitle>
            <CardDescription className="text-xs">
              Hechos contables pasados y botones individuales en reposo.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground space-y-1.5">
            <p>
              • <strong>Estados:</strong> <code>Anulada (NC)</code> y <code>Anulada manual</code> <strong>NO deben ser rojo chillón</strong>. Son registros históricos pasados; van en tonos pasteles suaves (rose desaturado o pizarra neutro).
            </p>
            <p>
              • <strong>Botón fila:</strong> <code>[🗑️ Anular]</code> con cuerpo pastel visible (nunca blanco), pero sin gritar alarma de muerte.
            </p>
          </CardContent>
        </Card>

        {/* Nivel 2 */}
        <Card className="border-border/60 bg-amber-50/20 dark:bg-amber-950/10 border-amber-200 dark:border-amber-900/40">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">Nivel 2</span>
              <Badge className="bg-amber-500 text-white font-medium">Medio / Alerta</Badge>
            </div>
            <CardTitle className="text-base text-amber-900 dark:text-amber-300">Destructivo Medio (Atención)</CardTitle>
            <CardDescription className="text-xs text-amber-800/80 dark:text-amber-400/80">
              Requiere revisión del usuario o acción de lote previa.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground space-y-1.5">
            <p>
              • <strong>Estado Error:</strong> <strong>NO debe ser rojo nuclear</strong>. Un error de envío al SII es una advertencia que se puede reintentar; va en naranja/ámbar o coral de advertencia.
            </p>
            <p>
              • <strong>Botón Masivo:</strong> <code>Anular selección (N)</code> va en tono coral mate o ámbar, indicando intención sin simular el disparo nuclear final.
            </p>
          </CardContent>
        </Card>

        {/* Nivel 3 */}
        <Card className="border-rose-300 bg-rose-50/30 dark:bg-rose-950/20 dark:border-rose-900/60 shadow-xs">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-rose-600 font-bold">Nivel 3</span>
              <Badge variant="destructive" className="bg-red-600 font-bold animate-pulse">Destructivo Destructivo</Badge>
            </div>
            <CardTitle className="text-base text-rose-800 dark:text-rose-400">Rojo Nuclear Exclusivo</CardTitle>
            <CardDescription className="text-xs text-rose-700/80 dark:text-rose-400/80">
              Impacto fiscal real e irreversible ante el Fisco.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-xs text-rose-700/80 dark:text-rose-400/80 space-y-1.5">
            <p>
              • <strong>ÚNICO LUGAR:</strong> El botón final dentro del modal: <code>⚠️ Confirmar y Emitir Nota de Crédito</code>.
            </p>
            <p>
              • Como la tabla y los estados NO gastaron el rojo nuclear, este botón destaca inmediatamente como el único punto de peligro crítico.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Selector de Propuestas de Calibración */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 mb-1">
            <Layers className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Elige una Propuesta de Calibración de Niveles</CardTitle>
          </div>
          <CardDescription>
            Haz clic en cada propuesta para ver cómo cambian los Badges de Estado (Error, Anulada, Emitido) y las Acciones en la tabla en vivo.
          </CardDescription>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3">
            {/* Propuesta 1 */}
            <button
              onClick={() => setSelectedPropuesta("1")}
              className={`p-3.5 rounded-lg border text-left transition-all cursor-pointer ${
                selectedPropuesta === "1"
                  ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                  : "border-border hover:bg-muted/50"
              }`}
            >
              <div className="font-semibold text-sm mb-1 flex items-center justify-between">
                <span>Propuesta 1 (Recomendada)</span>
                {selectedPropuesta === "1" && <CheckCircle2 className="h-4 w-4 text-primary" />}
              </div>
              <p className="text-xs text-muted-foreground mb-2.5">
                Semántica Calibrada: Anuladas en pasteles suaves (Piola), Error en Naranja/Coral (Medio), y Rojo reservado al Modal.
              </p>
              <div className="flex gap-1.5 flex-wrap">
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 border border-rose-200 text-rose-700">Anulada (NC)</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-100 border border-orange-300 text-orange-800">Error</span>
              </div>
            </button>

            {/* Propuesta 2 */}
            <button
              onClick={() => setSelectedPropuesta("2")}
              className={`p-3.5 rounded-lg border text-left transition-all cursor-pointer ${
                selectedPropuesta === "2"
                  ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                  : "border-border hover:bg-muted/50"
              }`}
            >
              <div className="font-semibold text-sm mb-1 flex items-center justify-between">
                <span>Propuesta 2 (Pizarra Contable)</span>
                {selectedPropuesta === "2" && <CheckCircle2 className="h-4 w-4 text-primary" />}
              </div>
              <p className="text-xs text-muted-foreground mb-2.5">
                Histórico Neutro: Todo documento anulado queda archivado en gris pizarra neutro. El error queda en rose suave.
              </p>
              <div className="flex gap-1.5 flex-wrap">
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 border border-slate-300 text-slate-700">Anulada (NC)</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-100 border border-rose-300 text-rose-800">Error</span>
              </div>
            </button>

            {/* Propuesta 3 */}
            <button
              onClick={() => setSelectedPropuesta("3")}
              className={`p-3.5 rounded-lg border text-left transition-all cursor-pointer ${
                selectedPropuesta === "3"
                  ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                  : "border-border hover:bg-muted/50"
              }`}
            >
              <div className="font-semibold text-sm mb-1 flex items-center justify-between">
                <span>Propuesta 3 (Alto Contraste)</span>
                {selectedPropuesta === "3" && <CheckCircle2 className="h-4 w-4 text-primary" />}
              </div>
              <p className="text-xs text-muted-foreground mb-2.5">
                Outline Marcado con Borde 2px para estados y rojo sólido solo para errores fatales.
              </p>
              <div className="flex gap-1.5 flex-wrap">
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 border-2 border-rose-400 text-rose-800">Anulada (NC)</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-500 text-white">Error</span>
              </div>
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Selector de Botones de Fila */}
          <div className="border-t pt-4 mb-4">
            <h3 className="text-sm font-semibold mb-2">Estilo de Botones de Acción en Fila:</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
              <button
                onClick={() => setSelectedVariant("C")}
                className={`p-2.5 rounded-md border text-left transition-all cursor-pointer ${
                  selectedVariant === "C" ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border hover:bg-muted/50"
                }`}
              >
                <div className="text-xs font-semibold flex items-center justify-between mb-1">
                  <span>Variante C (Texto + Icono)</span>
                  {selectedVariant === "C" && <CheckCircle2 className="h-3.5 w-3.5 text-primary" />}
                </div>
                <div className="flex gap-1.5">
                  <span className="px-1.5 py-0.5 rounded bg-teal-100 border border-teal-300 text-[10px] text-teal-800 font-semibold">Ver</span>
                  <span className="px-1.5 py-0.5 rounded bg-rose-100 border border-rose-300 text-[10px] text-rose-800 font-semibold">Anular</span>
                </div>
              </button>

              <button
                onClick={() => setSelectedVariant("A")}
                className={`p-2.5 rounded-md border text-left transition-all cursor-pointer ${
                  selectedVariant === "A" ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border hover:bg-muted/50"
                }`}
              >
                <div className="text-xs font-semibold flex items-center justify-between mb-1">
                  <span>Variante A (Pills)</span>
                  {selectedVariant === "A" && <CheckCircle2 className="h-3.5 w-3.5 text-primary" />}
                </div>
                <div className="flex gap-1.5">
                  <span className="h-4 w-4 rounded bg-teal-100 border border-teal-300 flex items-center justify-center text-[9px] text-teal-800">👁️</span>
                  <span className="h-4 w-4 rounded bg-rose-100 border border-rose-300 flex items-center justify-center text-[9px] text-rose-800">🗑️</span>
                </div>
              </button>

              <button
                onClick={() => setSelectedVariant("B")}
                className={`p-2.5 rounded-md border text-left transition-all cursor-pointer ${
                  selectedVariant === "B" ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border hover:bg-muted/50"
                }`}
              >
                <div className="text-xs font-semibold flex items-center justify-between mb-1">
                  <span>Variante B (Outline)</span>
                  {selectedVariant === "B" && <CheckCircle2 className="h-3.5 w-3.5 text-primary" />}
                </div>
                <div className="flex gap-1.5">
                  <span className="h-4 w-4 rounded border-2 border-teal-600 bg-white flex items-center justify-center text-[9px] text-teal-700">👁️</span>
                  <span className="h-4 w-4 rounded border-2 border-rose-500 bg-white flex items-center justify-center text-[9px] text-rose-600">🗑️</span>
                </div>
              </button>

              <button
                onClick={() => setSelectedVariant("D")}
                className={`p-2.5 rounded-md border text-left transition-all cursor-pointer ${
                  selectedVariant === "D" ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border hover:bg-muted/50"
                }`}
              >
                <div className="text-xs font-semibold flex items-center justify-between mb-1">
                  <span>Variante D (Sólidos)</span>
                  {selectedVariant === "D" && <CheckCircle2 className="h-3.5 w-3.5 text-primary" />}
                </div>
                <div className="flex gap-1.5">
                  <span className="h-4 w-4 rounded bg-slate-700 text-white flex items-center justify-center text-[9px]">👁️</span>
                  <span className="h-4 w-4 rounded bg-rose-600 text-white flex items-center justify-center text-[9px]">🗑️</span>
                </div>
              </button>
            </div>
          </div>

          {/* Barra de Lote de Demostración */}
          <div className="flex items-center justify-between bg-muted/40 p-3 rounded-lg mb-4 border">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">
                Documentos seleccionados: {selectedDocs.size} de {DOCS_DEMO.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {selectedDocs.size > 0 && (
                <Button
                  size="sm"
                  onClick={() => toast.success(`Descargando ZIP con ${selectedDocs.size} documentos...`)}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5 shadow-sm h-8"
                >
                  <Download className="h-4 w-4" />
                  Descargar ZIP ({selectedDocs.size})
                </Button>
              )}
              {Array.from(selectedDocs).some(
                (f) => DOCS_DEMO.find((d) => d.folio === f)?.estado === "EMITIDO"
              ) && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setModalOpen(true)}
                  className={`text-white gap-1.5 shadow-sm h-8 font-medium ${
                    selectedPropuesta === "1" ? "bg-rose-600 hover:bg-rose-700" : selectedPropuesta === "2" ? "bg-amber-600 hover:bg-amber-700" : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  <Trash2 className="h-4 w-4" />
                  Anular selección (
                  {
                    Array.from(selectedDocs).filter(
                      (f) => DOCS_DEMO.find((d) => d.folio === f)?.estado === "EMITIDO"
                    ).length
                  }
                  )
                </Button>
              )}
              <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => toast.info("Actualizando tabla...")}>
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Tabla de Demostración */}
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      checked={selectedDocs.size === DOCS_DEMO.length}
                      onChange={() => {
                        if (selectedDocs.size === DOCS_DEMO.length) setSelectedDocs(new Set());
                        else setSelectedDocs(new Set(DOCS_DEMO.map((d) => d.folio)));
                      }}
                      className="h-4 w-4 rounded accent-teal-600 cursor-pointer"
                    />
                  </TableHead>
                  <TableHead>Folio</TableHead>
                  <TableHead>Tipo DTE</TableHead>
                  <TableHead>Receptor</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                  <TableHead>Estado (Propuesta {selectedPropuesta})</TableHead>
                  <TableHead className="w-44 text-left">Acciones ({selectedVariant})</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {DOCS_DEMO.map((doc) => (
                  <TableRow
                    key={doc.folio}
                    className="cursor-pointer hover:bg-muted/40 transition-colors"
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.closest("button") || target.closest("input")) return;
                      toggleSelect(doc.folio);
                    }}
                  >
                    <TableCell className="w-10">
                      <input
                        type="checkbox"
                        checked={selectedDocs.has(doc.folio)}
                        onChange={() => toggleSelect(doc.folio)}
                        className="h-4 w-4 rounded accent-teal-600 cursor-pointer"
                      />
                    </TableCell>
                    <TableCell className="font-mono font-medium">{doc.folio === 0 ? "0 (Fallida)" : doc.folio}</TableCell>
                    <TableCell className="text-xs">{doc.tipo}</TableCell>
                    <TableCell className="text-sm font-medium">{doc.receptor}</TableCell>
                    <TableCell className="text-right font-medium">${doc.monto.toLocaleString("es-CL")}</TableCell>
                    <TableCell>{renderBadge(doc.estado)}</TableCell>
                    <TableCell>{renderActionButtons(doc)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Simulador del Modal Nuclear */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md border-rose-300 dark:border-rose-900/60 shadow-2xl">
          <DialogHeader>
            <div className="flex items-center gap-2 text-rose-600 mb-1">
              <ShieldAlert className="h-6 w-6" />
              <DialogTitle className="text-lg">Emitir Nota de Crédito para Anular</DialogTitle>
            </div>
            <DialogDescription className="text-xs leading-relaxed">
              Estás a punto de emitir una <strong>Nota de Crédito (DTE 61)</strong> timbrada legalmente ante el SII para anular el documento. Esta acción es <span className="text-rose-600 font-semibold underline">estrictamente irreversible</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="p-3 bg-rose-50/80 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 rounded-md text-xs text-rose-900 dark:text-rose-300 space-y-1">
            <p className="font-semibold flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4 text-rose-600" />
              Confirmación de Nivel 3 (Destrucción Legal)
            </p>
            <p className="text-[11px] text-muted-foreground dark:text-rose-300/80">
              Aquí vive el único botón Rojo Nuclear de la aplicación.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0 mt-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              className="bg-red-600 hover:bg-red-700 text-white font-bold shadow-md gap-1.5 animate-pulse"
              onClick={() => {
                toast.error("Simulación: Nota de Crédito emitida exitosamente ante el SII");
                setModalOpen(false);
              }}
            >
              ⚠️ Confirmar y Emitir Nota de Crédito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
